import { UnorderedListOutlined } from '@ant-design/icons';
import { Button, Empty, Spin, Tag } from 'antd';
import { observer } from 'mobx-react-lite';
import { useId, useState } from 'react';
import { defaultStyles, JsonView } from 'react-json-view-lite';
import { UserIdMenu } from '../../components/common/UserIdMenu';
import 'react-json-view-lite/dist/index.css';
import type {
  InspectionMessage,
  InspectionSnapshot,
  InspectionUser,
} from '../../services/roomInspectorApi';
import type { RoomInspectorStore } from '../../stores/roomInspectorStore';
import {
  groupMessages,
  isProcessMessage,
  isThinking,
  isTool,
  messageText,
  type TranscriptTurnBlock,
} from './groupMessages';
import { MessageMarkdown } from './MessageMarkdown';
import { MessageTime } from './MessageTime';
import { ParticipantAvatar } from './ParticipantAvatar';
import { agentParticipant, userParticipant } from './participantIdentity';
import {
  hasTurnError,
  isBlockingMessage,
  turnPresentation,
} from './turnPresentation';
import { useTranscriptHistory } from './useTranscriptHistory';

export function ReadOnlyJSON({ value }: { value: unknown }) {
  return value && typeof value === 'object' ? (
    <div className="inspector-json">
      <JsonView data={value} style={defaultStyles} />
    </div>
  ) : (
    <pre>{String(value ?? '')}</pre>
  );
}

function DeliveryCard({
  value,
  onPreview,
}: {
  value: unknown;
  onPreview: (id: string) => void;
}) {
  const result = value as {
    message?: string;
    items?: {
      artifact?: { id?: string; artifactId?: string; displayName?: string };
    }[];
  };
  return (
    <div className="inspector-delivery">
      <strong>交付产物</strong>
      {result.message && <p>{result.message}</p>}
      {result.items?.map((item, index) => {
        const id = item.artifact?.id || item.artifact?.artifactId;
        return id ? (
          <Button key={id} type="link" onClick={() => onPreview(id)}>
            {item.artifact?.displayName || `产物 ${index + 1}`}
          </Button>
        ) : null;
      })}
      <details>
        <summary>交付详情</summary>
        <ReadOnlyJSON value={value} />
      </details>
    </div>
  );
}

function UserMessage({
  message,
  users,
}: {
  message: InspectionMessage;
  users: Map<string, InspectionUser>;
}) {
  const participant = userParticipant(message.actorUserId, users);
  const text = messageText(message.payload);
  return (
    <article
      className="inspector-message is-user"
      data-message-id={message.messageId}
    >
      <ParticipantAvatar participant={participant} />
      <div className="inspector-message-bubble inspector-time-host">
        <div className="inspector-message-heading">
          <UserIdMenu userId={participant.id}>
            <strong title={participant.id || undefined}>
              {participant.name}
            </strong>
          </UserIdMenu>
          <span className="inspector-speaker-kind">用户</span>
        </div>
        {text ? (
          <MessageMarkdown text={text} />
        ) : (
          <details>
            <summary>消息详情</summary>
            <ReadOnlyJSON value={message.payload} />
          </details>
        )}
        <MessageTime value={message.occurredAtUnixMs} />
      </div>
    </article>
  );
}

function ProcessMessage({ message }: { message: InspectionMessage }) {
  const thinking = isThinking(message.kind);
  const tool = isTool(message.kind);
  const payload = message.payload as { name?: string; toolName?: string };
  return (
    <details
      className={
        thinking
          ? 'inspector-thinking inspector-time-host'
          : tool
            ? 'inspector-tool inspector-time-host'
            : 'inspector-event inspector-time-host'
      }
    >
      <summary>
        {thinking
          ? '思考过程'
          : tool
            ? payload?.name || payload?.toolName || message.kind
            : '会话事件'}
        {!thinking && <span>{message.status}</span>}
        <MessageTime value={message.occurredAtUnixMs} />
      </summary>
      {thinking ? (
        <MessageMarkdown text={messageText(message.payload)} />
      ) : (
        <ReadOnlyJSON value={message.payload} />
      )}
    </details>
  );
}

function AgentText({
  message,
  final,
}: {
  message: InspectionMessage;
  final?: boolean;
}) {
  const payload = message.payload as { detail?: string };
  const text = messageText(message.payload) || payload?.detail;
  return (
    <div
      className="inspector-agent-text inspector-time-host"
      data-message-id={message.messageId}
      data-final-summary={final || undefined}
      role={isBlockingMessage(message) ? 'alert' : undefined}
    >
      {text ? (
        <MessageMarkdown text={text} />
      ) : (
        <details>
          <summary>消息详情</summary>
          <ReadOnlyJSON value={message.payload} />
        </details>
      )}
      <MessageTime value={message.occurredAtUnixMs} />
    </div>
  );
}

function TurnContent({
  block,
  messages,
  presentation,
  users,
}: {
  block: TranscriptTurnBlock;
  messages: InspectionMessage[];
  presentation: ReturnType<typeof turnPresentation>;
  users: Map<string, InspectionUser>;
}) {
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const counts = [
    block.callCount && `${block.callCount} 次工具调用`,
    block.thinkingCount && `${block.thinkingCount} 段思考`,
    presentation.replyCount && `${presentation.replyCount} 条中间回复`,
    !block.callCount &&
      block.toolResultCount &&
      `${block.toolResultCount} 条工具结果`,
    presentation.eventCount && `${presentation.eventCount} 条会话事件`,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <>
      {presentation.workMessages.length > 0 && (
        <button
          type="button"
          className="inspector-process inspector-time-host"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => setExpanded(!expanded)}
        >
          <UnorderedListOutlined aria-hidden="true" />
          {counts}
          <MessageTime
            value={
              presentation.workMessages[presentation.workMessages.length - 1]
                ?.occurredAtUnixMs
            }
          />
        </button>
      )}
      {presentation.awaitingFinal && (
        <p className="inspector-summary-pending">
          向下滚动继续阅读，最终回复尚在后续消息中
        </p>
      )}
      <div id={contentId} className="inspector-turn-content">
        {messages.map((message) => {
          const work = presentation.workIds.has(message.messageId);
          return (
            <div
              key={message.messageId}
              className={work ? 'inspector-turn-work-item' : undefined}
              data-transcript-message-id={message.messageId}
              hidden={work && !expanded}
            >
              {message.role === 'user' ? (
                <UserMessage message={message} users={users} />
              ) : isProcessMessage(message) && !isBlockingMessage(message) ? (
                <ProcessMessage message={message} />
              ) : (
                <AgentText
                  message={message}
                  final={message.messageId === presentation.finalMessageId}
                />
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function ReadOnlyInteraction({
  item,
}: {
  item: InspectionSnapshot['latestTurnInteractions'][number];
}) {
  return (
    <details
      className="inspector-tools"
      open={['pending', 'waiting'].includes(item.status) || undefined}
    >
      <summary>
        {item.kind} · {item.status} · 只读
      </summary>
      <ReadOnlyJSON value={{ input: item.input, output: item.output }} />
    </details>
  );
}

export const SessionTranscript = observer(
  ({
    store,
    users,
  }: {
    store: RoomInspectorStore;
    users: Map<string, InspectionUser>;
  }) => {
    const snapshot = store.snapshot;
    const { listRef, contentRef, loadMore, ...historyEvents } =
      useTranscriptHistory(store);
    if (store.sessionLoading)
      return (
        <div className="inspector-placeholder">
          <Spin tip="正在读取会话" />
        </div>
      );
    if (!snapshot)
      return (
        <div className="inspector-placeholder">
          <Empty description={store.sessionError || '从左侧选择一个会话'} />
          {store.sessionError && (
            <Button onClick={() => store.selectSession(store.query.sessionId)}>
              重试
            </Button>
          )}
        </div>
      );
    const session = snapshot.session;
    const agent = agentParticipant(session, users);
    const blocks = groupMessages(snapshot.messages, snapshot.turns);
    const lastBlockByTurn = new Map(
      blocks
        .filter((block) => block.turnId)
        .map((block) => [block.turnId, block.id]),
    );
    return (
      <section className="inspector-transcript" aria-label="会话消息">
        <div className="inspector-session-header">
          <h2
            title={`${session.title || '未命名会话'} · ${agent.name} · ${session.settings?.model || '默认模型'}`}
          >
            {session.title || '未命名会话'}
          </h2>
          <div>
            <Tag>{session.status || 'idle'}</Tag>
            {session.accessMode === 'private' && (
              <Tag color="gold">私密会话</Tag>
            )}
            <Button
              size="small"
              onClick={() => store.selectSession(store.query.sessionId)}
            >
              刷新
            </Button>
          </div>
        </div>
        <div className="inspector-history-overlay">
          {store.historyLoading && (
            <output className="inspector-history-status" aria-live="polite">
              <Spin size="small" /> 正在加载后续消息
            </output>
          )}
          {store.sessionError && (
            <div role="alert" className="inspector-history-status">
              {store.sessionError}
              <Button size="small" type="link" onClick={() => loadMore(true)}>
                重试加载
              </Button>
            </div>
          )}
        </div>
        <div
          className="inspector-message-list"
          ref={listRef}
          {...historyEvents}
          role="log"
          aria-label="消息记录"
          aria-live="off"
          // biome-ignore lint/a11y/noNoninteractiveTabindex: Keyboard users must be able to focus and scroll this history.
          tabIndex={0}
        >
          <div className="inspector-message-content" ref={contentRef}>
            {!snapshot.messages.length && (
              <Empty description="此会话暂无消息" />
            )}
            {blocks.map((block) => {
              const firstAgentIndex = block.messages.findIndex(
                (message) => message.role !== 'user',
              );
              const leadingUsers =
                firstAgentIndex < 0
                  ? block.messages
                  : block.messages.slice(0, firstAgentIndex);
              const remaining =
                firstAgentIndex < 0
                  ? []
                  : block.messages.slice(firstAgentIndex);
              const presentation = turnPresentation(
                block,
                session,
                snapshot.messages,
                Boolean(snapshot.historyNextCursor),
              );
              const agentMessages = remaining.filter(
                (message) => message.role !== 'user',
              );
              const phase =
                block.turn?.phase ||
                agentMessages[agentMessages.length - 1]?.status;
              const interactions = snapshot.latestTurnInteractions.filter(
                (item) =>
                  item.turnId &&
                  item.turnId === block.turnId &&
                  lastBlockByTurn.get(item.turnId) === block.id,
              );
              return (
                <section
                  className="inspector-turn"
                  data-turn-id={block.turnId}
                  key={`${session.agentSessionId}:${block.id}`}
                >
                  {leadingUsers.map((message) => (
                    <UserMessage
                      key={message.messageId}
                      message={message}
                      users={users}
                    />
                  ))}
                  {(remaining.length > 0 ||
                    Boolean(block.deliverResult) ||
                    interactions.length > 0) && (
                    <article className="inspector-message is-assistant inspector-agent-turn">
                      <ParticipantAvatar participant={agent} />
                      <div className="inspector-message-bubble">
                        <div className="inspector-message-heading inspector-agent-turn-header">
                          <strong title={agent.id || undefined}>
                            {agent.name}
                          </strong>
                          {agent.ownerName && (
                            <UserIdMenu userId={agent.ownerUserId} owner>
                              <span
                                className="inspector-message-owner"
                                title={agent.ownerUserId}
                              >
                                所属：{agent.ownerName}
                              </span>
                            </UserIdMenu>
                          )}
                          {phase && (
                            <small>
                              {phase}
                              {block.turn?.outcome &&
                                ` · ${block.turn.outcome}`}
                            </small>
                          )}
                          {!block.turnId && <small>轮次未记录</small>}
                          {block.continuation && <small>同轮继续</small>}
                        </div>
                        <TurnContent
                          block={block}
                          messages={remaining}
                          presentation={presentation}
                          users={users}
                        />
                        {hasTurnError(block.turn?.error) &&
                          lastBlockByTurn.get(block.turnId) === block.id && (
                            <div className="inspector-turn-error" role="alert">
                              <strong>本轮错误</strong>
                              <ReadOnlyJSON value={block.turn?.error} />
                            </div>
                          )}
                        {Boolean(block.deliverResult) && (
                          <DeliveryCard
                            value={block.deliverResult}
                            onPreview={store.openPreview}
                          />
                        )}
                        {interactions.map((item) => (
                          <ReadOnlyInteraction
                            key={item.requestId}
                            item={item}
                          />
                        ))}
                      </div>
                    </article>
                  )}
                </section>
              );
            })}
          </div>
          {snapshot.latestTurnInteractions
            .filter(
              (item) =>
                !snapshot.historyNextCursor &&
                (!item.turnId || !lastBlockByTurn.has(item.turnId)),
            )
            .map((item) => (
              <ReadOnlyInteraction key={item.requestId} item={item} />
            ))}
        </div>
      </section>
    );
  },
);
